import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import EligibilityCheckForm from "@/components/EligibilityCheckForm";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Link } from "@/lib/router-compat";
import {
  Activity,
  HeartPulse,
  ShieldAlert,
  Clock,
  ArrowRight,
  PhoneCall,
  CheckCircle2,
  HelpCircle,
} from "lucide-react";

export default function KetamineTherapyForChronicPainPage() {
  const publishedDate = "2024-01-30";
  const updatedDate = "2026-11-03";
  const heroImage =
    "https://neurowellnesstms.com/wp-content/uploads/2024/01/ketamine-therapy-for-chronic-pain.jpg";

  const faqs = [
    {
      q: "How does Ketamine therapy work for chronic pain relief?",
      a: "Ketamine acts as an NMDA receptor antagonist in the central nervous system. By blocking NMDA receptors, ketamine reduces pain signaling, interrupts central sensitization (wind-up pain), and resets pain perception pathways.",
    },
    {
      q: "What conditions can be treated with Ketamine pain infusions?",
      a: "Ketamine infusions are used for neuropathic pain, Complex Regional Pain Syndrome (CRPS / RSD), fibromyalgia, chronic migraines, phantom limb pain, and treatment-resistant central pain syndromes.",
    },
    {
      q: "How long does relief last after Ketamine infusion therapy?",
      a: "Relief duration varies by individual and protocol. Many patients experience relief lasting several weeks to months following an initial series of infusions, with periodic maintenance protocols as needed.",
    },
    {
      q: "Is Ketamine therapy safe for chronic pain management?",
      a: "When administered under board-certified medical supervision with continuous vital sign monitoring, Ketamine infusion therapy has an established safety profile with well-tolerated side effects.",
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Ketamine Therapy for Chronic Pain: A Powerful Solution | Neuro Wellness"
        description="Discover how Ketamine infusion therapy targets NMDA receptors to disrupt chronic pain signaling, CRPS, fibromyalgia, and neuropathic conditions. Expert medical care in Coral Springs, FL."
        canonical="/ketamine-therapy-for-chronic-pain-a-powerful-solution"
        keywords="ketamine therapy chronic pain, ketamine pain infusions Coral Springs FL, NMDA antagonist pain relief, CRPS ketamine, fibromyalgia ketamine treatment"
        ogImage={heroImage}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                "@id":
                  "https://neurowellnesstms.com/ketamine-therapy-for-chronic-pain-a-powerful-solution#webpage",
                name: "Ketamine Therapy for Chronic Pain: A Powerful Solution",
                url: "https://neurowellnesstms.com/ketamine-therapy-for-chronic-pain-a-powerful-solution",
                inLanguage: "en-US",
                datePublished: publishedDate,
                dateModified: updatedDate,
                description:
                  "Detailed clinical overview of Ketamine infusion therapy for chronic pain conditions including CRPS, neuropathic pain, and fibromyalgia.",
                publisher: {
                  "@type": "MedicalOrganization",
                  name: "Neuro Wellness TMS Centers of America",
                  url: "https://neurowellnesstms.com",
                  telephone: "+1-954-827-9793",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: "9734 West Sample Road, 2nd Floor, Suite B",
                    addressLocality: "Coral Springs",
                    addressRegion: "FL",
                    postalCode: "33065",
                    addressCountry: "US",
                  },
                },
              },
              {
                "@type": "FAQPage",
                "@id":
                  "https://neurowellnesstms.com/ketamine-therapy-for-chronic-pain-a-powerful-solution#faq",
                mainEntity: faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        }}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* HERO */}
        <section className="relative overflow-hidden bg-primary-deep text-primary-foreground py-12 sm:py-16 lg:py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-deep via-primary-deep/95 to-slate-900" />
          <div className="container-px relative mx-auto max-w-6xl">
            <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-12">
              <div className="order-2 lg:order-1 lg:col-span-7">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3.5 py-1 text-xs font-semibold text-accent backdrop-blur-sm">
                    <Activity className="h-4 w-4" />
                    Interventional Pain Management
                  </span>
                  <span className="text-xs text-primary-foreground/75 flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    Medically Reviewed · Updated Jan 2024
                  </span>
                </div>

                <h1 className="font-headings text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-white">
                  Ketamine Therapy for Chronic Pain:{" "}
                  <span className="text-accent">A Powerful Solution</span>
                </h1>

                <p className="mt-5 text-base sm:text-lg text-primary-foreground/90 leading-relaxed max-w-2xl">
                  Chronic pain changes the way your nervous system processes
                  signals. Ketamine infusion therapy targets NMDA pain receptors
                  to disrupt central sensitization and provide lasting relief
                  when conventional treatments fall short.
                </p>

                <div className="mt-6 flex flex-wrap gap-4 text-xs sm:text-sm text-primary-foreground/80">
                  <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-md backdrop-blur-sm border border-white/10">
                    <HeartPulse className="h-4 w-4 text-accent" />
                    <span>NMDA Receptor Modulation</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-md backdrop-blur-sm border border-white/10">
                    <ShieldAlert className="h-4 w-4 text-accent" />
                    <span>Physician-Monitored Infusions</span>
                  </div>
                </div>
              </div>

              <div className="order-1 lg:order-2 lg:col-span-5">
                <div className="rounded-2xl border border-white/15 bg-white p-5 sm:p-6 shadow-2xl text-foreground">
                  <div className="mb-4">
                    <span className="text-xs font-bold uppercase tracking-wider text-accent">
                      Chronic Pain Assessment
                    </span>
                    <h2 className="text-xl font-bold text-foreground mt-1">
                      Check Ketamine Eligibility
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1">
                      Speak with our medical team in Coral Springs, FL to learn
                      if Ketamine pain protocol is right for you.
                    </p>
                  </div>
                  <EligibilityCheckForm
                    source="ketamine-therapy-for-chronic-pain-a-powerful-solution"
                    variant="card"
                    redirectTo="/thank-you-consultation"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BREADCRUMBS */}
        <div className="container-px mx-auto max-w-4xl pt-6">
          <Breadcrumbs
            crumbs={[
              { name: "Resources", href: "/articles" },
              {
                name: "Ketamine for Chronic Pain",
                href: "/ketamine-therapy-for-chronic-pain-a-powerful-solution",
              },
            ]}
          />
        </div>

        {/* MAIN ARTICLE BODY */}
        <article className="container-px mx-auto max-w-4xl py-6 space-y-12">
          {/* Section 1 */}
          <section className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground">
              The Science of Ketamine for Pain Relief
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              When chronic pain persists for months or years, spinal cord and
              brain pathways undergo "central sensitization" — becoming
              hyper-reactive to pain signals. Ketamine works by blocking
              N-methyl-D-aspartate (NMDA) receptors in the central nervous
              system, effectively resetting overactive pain circuits and
              lowering hypersensitivity.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 pt-4">
              <div className="rounded-xl border p-5 bg-card space-y-2">
                <h3 className="font-bold text-lg text-foreground">
                  NMDA Receptor Inhibition
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Prevents glutamate from hyper-stimulating pain pathways,
                  decreasing persistent wind-up pain and burning sensations.
                </p>
              </div>
              <div className="rounded-xl border p-5 bg-card space-y-2">
                <h3 className="font-bold text-lg text-foreground">
                  Anti-Inflammatory Properties
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Reduces pro-inflammatory cytokines in neuro-glial tissue,
                  suppressing localized and systemic neuro-inflammation.
                </p>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground">
              Conditions Treated with Ketamine Infusions
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Ketamine therapy is particularly effective for refractory pain
              conditions that have not responded to opioids, physical therapy,
              or epidural injections:
            </p>

            <ul className="grid sm:grid-cols-2 gap-3 pt-2 text-sm text-foreground">
              {[
                "Complex Regional Pain Syndrome (CRPS / RSD)",
                "Neuropathic Pain & Diabetic Neuropathy",
                "Fibromyalgia & Central Sensitization",
                "Chronic Intractable Migraines",
                "Post-Surgical or Post-Traumatic Nerve Pain",
                "Spinal Cord Injury Pain & Phantom Limb Pain",
              ].map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 bg-muted/30 p-3 rounded-lg border"
                >
                  <CheckCircle2 className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Section 3 */}
          <section className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground">
              What to Expect During Ketamine Infusion Treatment
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Infusions are administered in private medical suites with
              continuous monitoring of pulse, blood pressure, and oxygen levels.
              Patients relax in reclining chairs while medical staff oversee
              every detail of the protocol.
            </p>
          </section>

          {/* FAQ SECTION */}
          <section className="space-y-6 pt-6 border-t">
            <div className="flex items-center gap-2 text-accent">
              <HelpCircle className="h-6 w-6" />
              <h2 className="text-2xl font-bold text-foreground">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="rounded-xl border p-5 bg-card">
                  <h3 className="text-base font-bold text-foreground mb-2">
                    {faq.q}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* RELATED PAGES */}
          <section className="space-y-4 pt-6 border-t">
            <h3 className="text-xl font-bold text-foreground">
              Related Chronic Pain Solutions
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link
                to="/ketamine-for-crps"
                className="p-4 rounded-xl border bg-card hover:border-accent transition-colors flex items-center justify-between"
              >
                <div>
                  <h4 className="font-bold text-sm text-foreground">
                    Ketamine for CRPS
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Targeted treatment for Complex Regional Pain Syndrome
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-accent" />
              </Link>
              <Link
                to="/ketamine-for-chronic-pain"
                className="p-4 rounded-xl border bg-card hover:border-accent transition-colors flex items-center justify-between"
              >
                <div>
                  <h4 className="font-bold text-sm text-foreground">
                    Chronic Pain Care Hub
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Overview of pain protocols in Coral Springs
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-accent" />
              </Link>
            </div>
          </section>

          {/* CTA BANNER */}
          <section className="rounded-2xl bg-gradient-to-br from-primary-deep to-slate-900 p-6 sm:p-8 text-white space-y-4">
            <h3 className="text-2xl font-bold">
              Find Break-Through Relief From Chronic Pain
            </h3>
            <p className="text-sm text-primary-foreground/90 leading-relaxed">
              Contact our medical team at Neuro Wellness TMS Centers of America
              to discuss your pain history and evaluate Ketamine therapy.
            </p>
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <a
                href="tel:954-827-9793"
                className="inline-flex items-center gap-2 rounded-lg bg-accent px-5 py-2.5 text-sm font-semibold text-slate-950 hover:bg-accent/90 transition-colors"
              >
                <PhoneCall className="h-4 w-4" /> Call 954-827-9793
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/20 transition-colors"
              >
                Request Pain Evaluation
              </Link>
            </div>
          </section>
        </article>
      </main>

      <Footer />
    </div>
  );
}
